import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";

const ACTION_OPTIONS = [
  { value: "", label: "Todas as ações" },
  { value: "LOGIN", label: "Entradas no sistema" },
  { value: "LOGOUT", label: "Saídas do sistema" },
  { value: "API_CALL", label: "Ações em andamento" },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatDuration = (ms) => {
  if (typeof ms !== "number" || Number.isNaN(ms)) return "-";
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  const hours = minutes / 60;
  return `${hours.toFixed(1)} h`;
};

const MetadataPreview = ({ metadata }) => {
  if (!metadata) return <span className="text-slate-400">-</span>;
  try {
    const json = typeof metadata === "string" ? JSON.parse(metadata) : metadata;
    return (
      <details className="text-xs text-slate-600">
        <summary className="cursor-pointer text-blue-600">Ver detalhes</summary>
        <pre className="mt-2 max-h-48 overflow-auto rounded bg-slate-900/80 p-2 text-[11px] text-slate-100">
          {JSON.stringify(json, null, 2)}
        </pre>
      </details>
    );
  } catch {
    return <span className="text-xs text-slate-600">{String(metadata)}</span>;
  }
};

export default function AuditLogs({ refreshKey }) {
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    action: "",
    search: "",
    userId: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        pageSize,
      };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== "") {
          params[key] = value;
        }
      });
      const response = await api.get("/audit/logs", { params });
      setLogs(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotal(Number(response.data?.total) || 0);
    } catch (err) {
      const message = err.response?.data?.error || "Falha ao carregar auditoria";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, pageSize]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, refreshKey]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    const cleared = { startDate: "", endDate: "", action: "", search: "", userId: "" };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setPage(1);
  };

  const canGoBack = page > 1;
  const canGoForward = page < totalPages;

  return (
    <div className="space-y-4 p-3">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Auditoria do sistema</h1>
        <p className="text-sm text-slate-600">
          Acompanhe entradas, saídas e ações realizadas por todos os usuários. Somente administradores podem visualizar estes registros.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-800">Filtros</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col text-sm text-slate-700">
            Data inicial
            <input
              type="date"
              value={filters.startDate}
              onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Data final
            <input
              type="date"
              value={filters.endDate}
              onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Usuário (ID)
            <input
              type="number"
              min="1"
              value={filters.userId}
              onChange={(event) => setFilters((prev) => ({ ...prev, userId: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
              placeholder="Ex.: 12"
            />
          </label>
          <label className="flex flex-col text-sm text-slate-700">
            Tipo de ação
            <select
              value={filters.action}
              onChange={(event) => setFilters((prev) => ({ ...prev, action: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
            >
              {ACTION_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col text-sm text-slate-700 md:col-span-2 lg:col-span-3">
            Buscar por descrição, caminho ou nome do usuário
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="mt-1 rounded border border-slate-300 px-3 py-2"
              placeholder="Ex.: /processes, João, formulário"
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={applyFilters}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
          >
            Limpar
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Registros encontrados</h2>
            <p className="text-sm text-slate-600">
              {loading
                ? "Carregando registros..."
                : total === 0
                ? "Nenhum registro encontrado para os filtros aplicados."
                : `${total} registro${total === 1 ? "" : "s"} localizado${total === 1 ? "" : "s"}.`}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <span>Itens por página</span>
            <select
              value={pageSize}
              onChange={(event) => {
                const newSize = Number(event.target.value);
                setPageSize(newSize);
                setPage(1);
              }}
              className="rounded border border-slate-300 px-2 py-1"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">{error}</p>}

        {!error && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Data/Hora</th>
                  <th className="px-3 py-2 text-left">Usuário</th>
                  <th className="px-3 py-2 text-left">Ação</th>
                  <th className="px-3 py-2 text-left">Descrição</th>
                  <th className="px-3 py-2 text-left">Recurso</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Duração</th>
                  <th className="px-3 py-2 text-left">Origem</th>
                  <th className="px-3 py-2 text-left">Detalhes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                      Carregando auditoria...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                      Nenhum registro para exibir.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700">{formatDateTime(log.createdAt)}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {log.userName ? (
                          <span className="font-medium text-slate-800">{log.userName}</span>
                        ) : (
                          <span className="text-slate-500">Usuário #{log.userId ?? "-"}</span>
                        )}
                        {log.userId && (
                          <span className="ml-2 text-xs text-slate-500">ID {log.userId}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                          {log.action || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{log.description || "-"}</td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs text-slate-700">{log.method || "-"}</span>
                          <span className="truncate text-xs text-slate-500" title={log.path || undefined}>
                            {log.path || "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{log.statusCode ?? "-"}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDuration(log.durationMs)}</td>
                      <td className="px-3 py-2 text-slate-600">
                        <div className="flex flex-col">
                          <span className="text-xs text-slate-600" title={log.ip || undefined}>
                            {log.ip || "-"}
                          </span>
                          <span className="truncate text-[11px] text-slate-500" title={log.userAgent || undefined}>
                            {log.userAgent || ""}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <MetadataPreview metadata={log.metadata} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 pt-3 text-sm text-slate-700 md:flex-row">
          <div>
            Página {page} de {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => canGoBack && setPage((prev) => Math.max(1, prev - 1))}
              disabled={!canGoBack}
              className="rounded border border-slate-300 px-3 py-1 text-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => canGoForward && setPage((prev) => prev + 1)}
              disabled={!canGoForward}
              className="rounded border border-slate-300 px-3 py-1 text-sm transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
